<template>
    <div :class="b({ unavailable })">
        <dtg-action
            :class="b('card')"

            :href="dtgDashboardUrl"
            :title="`Ouvrir ${data.name}`"

            routed
        >
            <dtg-grid>
                <dtg-row>
                    <dtg-col
                        :class="b('col')"

                        size-xs="12"
                        size-lg
                    >
                        <dtg-spinner
                            v-if="fetching"

                            :class="b('spinner')"

                            :title="iconTitle"
                        />
                        <div
                            v-else

                            :class="[b('icon'), b('indicator-wrapper')]"

                            :title="iconTitle"
                        >
                            <dtg-icon
                                name="folder"

                                :class="b('indicator-icon')"
                            />
                            <dtg-icon
                                v-if="iconName"

                                :class="b('indicator')"

                                :name="iconName"
                            />
                        </div>
                        <span :class="b('text')">
                            {{ data.name }}
                        </span>
                    </dtg-col>
                    <dtg-col
                        :class="b('col', { visitDate: true, empty: !data.visitDate })"

                        size-xs="6"
                        :size-sm="data.endDate ? 'auto' : ''"
                        size-lg
                    >
                        <template v-if="data.visitDate">
                            <dtg-icon :class="b('icon')" name="calendar" />
                            <span>{{ formatDate(data.visitDate) }}</span>
                        </template>
                    </dtg-col>
                    <dtg-col
                        :class="b('col', { endDate: true, empty: !data.endDate })"

                        size-xs="6"
                        size-sm
                    >
                        <template v-if="data.endDate">
                            <dtg-icon :class="b('icon')" name="stopwatch" />
                            <span>{{ formatDate(data.endDate) }}</span>
                        </template>
                    </dtg-col>
                    <dtg-col
                        v-if="!data.visitDate && !data.endDate"

                        :class="b('col', { datePlaceholder: true })"
                    />
                    <dtg-col
                        :class="b('col', { status: true })"

                        size-xs="12"
                        size-sm
                    >
                        <dtg-mark :class="[b('icon'), b('status-mark')]" :view="validMarkView" />
                        {{ statusLabelText }}
                        <dtg-icon
                            v-if="isCheckVisible"

                            :class="b('icon')"

                            name="checkmark"
                            title="Valide"
                        />
                    </dtg-col>
                </dtg-row>
            </dtg-grid>
        </dtg-action>
        <div :class="b('actions')">
            <template v-if="sharedActionsVisible">
                <dtg-icon-action
                    :class="b('action')"

                    :disabled="trameReport.disabled"

                    name="download"

                    @click="trameReport.download()"
                />
                <dtg-icon-action
                    :class="b('action')"

                    name="dashboard"

                    v-bind="backofficeLinkProps"
                />
            </template>
            <dtg-icon-action
                v-if="!sharedActionsVisible || canRepairValidation"

                :class="b('action', { dropdown: true, open: isDropdownOpen })"

                name="kebab"

                @click="onDropdownClick($event)"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useRouter } from 'vue-router';

import {
    DtgAction,
    DtgCol,
    DtgGrid,
    DtgIcon,
    DtgIconAction,
    DtgIconName,
    DtgMark,
    DtgMarkView,
    DtgRow,
    DtgSpinner,
    getName,
    positionPopover,
    useBem,
    useBp,
} from 'dtg-components';
import { DtgStatus } from 'dtg-firebase/modules/dtg';
import { formatDate } from 'dtg-shared/utils/date';
import { toReadonlyRefs } from 'dtg-shared/utils/refs';
import { PdfType } from 'dtg-shared-report';

import { useDtgListItemSharedActions } from '@/composables/use-dtg-list-item-shared-actions';
import { useNetworkStatus } from '@/composables/use-network-status';

import { useStoreCollection, useStoreModule } from '@/store';

import { Route } from '@/router';

import { useListItemDropdown } from '../list-item-dropdown';

import styles from './style.module.scss';

export default defineComponent({
    name: getName(styles),

    components: {
        DtgGrid,
        DtgRow,
        DtgCol,
        DtgAction,
        DtgIcon,
        DtgMark,
        DtgIconAction,
        DtgSpinner,
    },

    props: {
        dtgId: {
            type: String,
            required: true,
        },
    },

    setup(props) {
        const { dtgId } = toReadonlyRefs(props);

        const bp = useBp();

        const {
            getters: { getItemThrows, getDtgStatusLabel, getIsAvailableOffline, getIsFetching },
        } = useStoreCollection('dtg');

        const { getters: rules } = useStoreModule('rules');

        const { offline } = useNetworkStatus();
        const router = useRouter();
        const dropdown = useListItemDropdown();

        const { backofficeLinkProps, reportDownload, repairValidation } = useDtgListItemSharedActions({
            dtgId,
        });

        const dtg = computed(() => getItemThrows(dtgId.value));
        const data = computed(() => dtg.value.data);

        const availableOffline = computed(() => getIsAvailableOffline(dtgId.value));
        const fetching = computed(() => getIsFetching(dtgId.value));
        const loaded = computed(() => dtg.value.loaded);

        const canRepairValidation = computed(() => rules.canRepairDtgValidation(dtg.value));
        const isCheckVisible = computed(() => dtg.value.valid && data.value.status === DtgStatus.opened);
        const sharedActionsVisible = computed(() => bp.from(bp.lg));

        const dtgDashboardUrl = computed(() => {
            return router.resolve({ name: Route.dashboard, params: { id: dtgId.value } });
        });

        const unavailable = computed(() => !availableOffline.value && offline.value && loaded.value);

        const iconName = computed<DtgIconName | null>(() => {
            switch (true) {
                case availableOffline.value:
                    return 'checkmarkCircle';

                case offline.value && loaded.value:
                    return 'closeCircle';

                default:
                    return null;
            }
        });

        const iconTitle = computed<string | null>(() => {
            switch (true) {
                case fetching.value:
                    return 'Téléchargement en cours';

                case availableOffline.value:
                    return 'Disponible hors connexion';

                case offline.value && loaded.value:
                    return 'Non disponible hors connexion';

                default:
                    return null;
            }
        });

        const validMarkView = computed(() => {
            const dtgStatusMarkViewMap: Record<DtgStatus, DtgMarkView> = {
                [DtgStatus.closed]: DtgMarkView.disabled,
                [DtgStatus.ready]: DtgMarkView.success,
                [DtgStatus.opened]: DtgMarkView.warning,
                [DtgStatus.planning]: DtgMarkView.disabled,
                [DtgStatus.onValidation]: DtgMarkView.notice,
            };

            return dtgStatusMarkViewMap[data.value.status];
        });

        const statusLabelText = computed(() => {
            const { status } = data.value;

            return getDtgStatusLabel({ status });
        });

        const trameReport = reportDownload(PdfType.trame);

        function onDropdownClick(event: Event): void {
            dropdown.open(
                {
                    dtgId: dtgId.value,
                    sharedActionsHidden: !sharedActionsVisible.value,
                    backofficeLinkProps,
                    canRepairValidation: canRepairValidation.value,
                    onRepairValidation: repairValidation,
                    canDownloadReport: !trameReport.disabled,
                    onDownloadReport: trameReport.download,
                },
                positionPopover(event, { position: { horizontal: 'right' } }),
            );
        }

        return {
            b: useBem(styles),

            data,
            dtgDashboardUrl,
            validMarkView,
            statusLabelText,
            isCheckVisible,
            isDropdownOpen: dropdown.isOpen,
            backofficeLinkProps,
            sharedActionsVisible,
            canRepairValidation,
            fetching,
            iconName,
            iconTitle,
            unavailable,
            trameReport,

            formatDate,
            onDropdownClick,
        };
    },
});
</script>
